<template>
  <!-- Horizontal step nodes -->
  <div v-if="variant === 'horizontal'" class="h-stepper" role="list">
    <div
      v-for="(step, i) in steps"
      :key="step.key || i"
      class="h-step"
      :class="stateClass(i)"
      role="listitem"
    >
      <button
        type="button"
        class="h-step__node"
        :disabled="!isDone(i)"
        :aria-current="i === activeIndex ? 'step' : null"
        @click="onNodeClick(i)"
      >
        <svg v-if="isDone(i)" viewBox="0 0 24 24" class="h-step__check"><path d="M20 6L9 17l-5-5"/></svg>
        <span v-else>{{ i + 1 }}</span>
      </button>
      <div class="h-step__label">{{ step.label }}</div>
      <div v-if="step.hint" class="h-step__hint">{{ step.hint }}</div>
    </div>
  </div>

  <!-- Vertical checklist variant -->
  <div v-else class="h-vsteps" role="list">
    <div
      v-for="(step, i) in steps"
      :key="step.key || i"
      class="h-vstep"
      :class="stateClass(i)"
      role="listitem"
    >
      <div class="h-vstep__node">
        <svg v-if="isDone(i)" viewBox="0 0 24 24" class="h-step__check"><path d="M20 6L9 17l-5-5"/></svg>
        <span v-else>{{ i + 1 }}</span>
      </div>
      <div class="h-vstep__text">
        <div class="h-vstep__t">{{ step.label }}</div>
        <div v-if="step.description" class="h-vstep__d" v-html="step.description"></div>
      </div>
    </div>
  </div>
</template>

<script>
// Wizard stepper — horizontal step nodes (done / active / todo) plus an
// optional vertical checklist variant. Matches design/components/wizard-steps.html.
// Completed steps are revisitable (emit 'step-click'); forward moves are gated
// by the parent (this component doesn't advance itself).
export default {
  name: 'HermesWizardStepper',
  props: {
    // [{ key, label, hint, done?, description? }]
    steps: { type: Array, required: true },
    // Index of the active step, or its key.
    current: { type: [Number, String], default: 0 },
    variant: { type: String, default: 'horizontal', validator: (v) => ['horizontal', 'vertical'].includes(v) },
  },
  emits: ['step-click'],
  computed: {
    activeIndex() {
      if (typeof this.current === 'number') return this.current;
      const idx = this.steps.findIndex((s) => s.key === this.current);
      return idx >= 0 ? idx : 0;
    },
  },
  methods: {
    isDone(i) {
      // Explicit per-step done wins; otherwise anything before the active step.
      if (typeof this.steps[i].done === 'boolean') return this.steps[i].done;
      return i < this.activeIndex;
    },
    stateClass(i) {
      if (this.isDone(i)) return 'is-done';
      if (i === this.activeIndex) return 'is-active';
      return 'is-todo';
    },
    onNodeClick(i) {
      if (this.isDone(i)) this.$emit('step-click', i);
    },
  },
};
</script>

<style scoped>
/* Horizontal */
.h-stepper { display: flex; align-items: flex-start; font-family: var(--h-font-body, system-ui); }
.h-step { flex: 1; display: flex; flex-direction: column; align-items: center; position: relative; text-align: center; }
.h-step:not(:last-child)::after {
  content: ''; position: absolute; top: 16px; left: 50%; width: 100%; height: 2px;
  background: var(--h-border, #E2E8F0); z-index: 0;
}
.h-step.is-done:not(:last-child)::after,
.h-step.is-active:not(:last-child)::after { background: var(--h-accent, #0EA5E9); }
.h-step__node {
  width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  font: inherit; font-weight: 600; font-size: 14px; z-index: 1; cursor: default;
  border: 2px solid var(--h-border-strong, #CBD5E1); background: var(--h-bg-card, #fff);
  color: var(--h-text-tertiary, #94A3B8);
}
.h-step.is-active .h-step__node {
  border-color: var(--h-accent, #0EA5E9); color: var(--h-accent-dark, #0369A1);
  background: var(--h-accent-light, #E0F2FE); box-shadow: var(--h-focus-ring);
}
.h-step.is-done .h-step__node {
  border-color: var(--h-accent, #0EA5E9); background: var(--h-accent, #0EA5E9); color: #fff; cursor: pointer;
}
.h-step.is-done .h-step__node:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-step__check { width: 16px; height: 16px; stroke: currentColor; fill: none; stroke-width: 2.5; }
.h-step__label { font-size: 12.5px; font-weight: 600; margin-top: 9px; color: var(--h-text-secondary, #64748B); }
.h-step.is-active .h-step__label { color: var(--h-text-primary, #0F172A); }
.h-step__hint { font-size: 11px; color: var(--h-text-tertiary, #94A3B8); margin-top: 2px; }

/* Vertical */
.h-vsteps {
  background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0);
  border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card);
  padding: 8px 22px; font-family: var(--h-font-body, system-ui);
}
.h-vstep { display: flex; gap: 16px; padding: 14px 0; position: relative; }
.h-vstep:not(:last-child)::after {
  content: ''; position: absolute; left: 15px; top: 34px; bottom: -14px; width: 2px; background: var(--h-border, #E2E8F0);
}
.h-vstep.is-done:not(:last-child)::after { background: var(--h-accent, #0EA5E9); }
.h-vstep__node {
  width: 32px; height: 32px; border-radius: 50%; flex: none; display: flex; align-items: center; justify-content: center;
  font-weight: 600; font-size: 13px; z-index: 1;
  border: 2px solid var(--h-border-strong, #CBD5E1); background: var(--h-bg-card, #fff); color: var(--h-text-tertiary, #94A3B8);
}
.h-vstep.is-done .h-vstep__node { background: var(--h-accent, #0EA5E9); border-color: var(--h-accent, #0EA5E9); color: #fff; }
.h-vstep.is-active .h-vstep__node { border-color: var(--h-accent, #0EA5E9); color: var(--h-accent-dark, #0369A1); background: var(--h-accent-light, #E0F2FE); }
.h-vstep__t { font-size: 14px; font-weight: 600; color: var(--h-text-primary, #0F172A); }
.h-vstep__d { font-size: 12.5px; color: var(--h-text-secondary, #64748B); margin-top: 2px; }
.h-vstep__d :deep(.mono) { font-family: var(--h-font-mono, monospace); }
</style>
